import {
  ClassExtended,
  CourseStructureCtx,
  DbUser,
  UserCtx,
} from "../../../../interfaces";
import {
  Box,
  CircularProgress,
  Divider,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import axios from "../../../../services/axios";
import { ButtonZoom } from "../../../v2/utils/ButtonZoom";
import React, { useContext, useEffect } from "react";
import { makeStyles } from "@mui/styles";
import {
  AccessListItems,
  ShareAccessModal,
} from "../../../v2/userManagment/ShareAccessModal";
import {
  addObservers,
  deleteObserver,
  formatDate,
  generateGtmEvent,
  getObservers,
  HandleDataLayer,
} from "../../../../Helpers";
import { toast } from "react-toastify";
import { UserContext } from "../../../../context/UserContext";
import { ErrorToast, SuccessToast } from "../../../v2/utils/Toast";
import { UserClassSettings } from "@backend-types";
import { Role } from "../../../../enums";
import { DatePicker } from "@mui/x-date-pickers";
import { CourseStructureContext } from "../../../../context/CourseStructureContext";
import { ClassRequest } from "../../../v2/userManagment/EditDetails/EditClass";

interface ClassDetailsProps {
  classDetails: ClassExtended;
  updateCachedClassSettings: (newSettings: UserClassSettings) => void;
}

const useStyle = makeStyles((theme) => ({
  headlines: {
    font: "normal normal 600 20px/27px proxima-soft !important",
    letterSpacing: "0.15px",
    color: theme.palette.text.primary,
  },
  subHeading: {
    font: "normal normal normal 16px/24px Roboto",
    color: theme.palette.text.primary,
  },
}));

const ClassDetailItems = (props: {
  title: string;
  value?: string | null;
  isValueVisible?: boolean;
}) => {
  const classes = useStyle();
  return (
    <Stack flexDirection={"column"} alignItems={"left"} gap={"8px"}>
      <Typography variant={"body2"} className={classes.headlines}>
        {props.title}
      </Typography>
      {props.isValueVisible && (
        <Typography variant={"body2"} className={classes.subHeading}>
          {props.value}
        </Typography>
      )}
    </Stack>
  );
};

interface ClassDetailEditableItemProps {
  onChange: (e: React.ChangeEvent<HTMLInputElement> | null | any) => void;
  value: string | null;
  disabled: boolean;
  label: string;
  placeholder: string;
  isVisible?: boolean;
  helperText?: string;
  variant: "datepicker" | "text" | "dropdown";
  children?: React.ReactNode;
}

export const ClassDetailEditableItem = (props: ClassDetailEditableItemProps) => {
  const {
    onChange,
    value,
    disabled,
    label,
    placeholder,
    isVisible = true,
    helperText,
    variant,
    children,
  } = props;

  const [calendarOpen, setCalendarOpen] = React.useState(false);

  if (isVisible && variant === "text") {
    return (
      <FormControl sx={{ marginTop: "16px" }}>
        <InputLabel htmlFor="component-outlined">{label}</InputLabel>
        <OutlinedInput
          disabled={disabled}
          id="component-outlined"
          placeholder={placeholder}
          aria-describedby="component-label-text"
          label={label}
          value={value}
          onChange={onChange}
        />
        <FormHelperText sx={{ fontFamily: "Roboto" }} id="component-label-text">
          {helperText}
        </FormHelperText>
      </FormControl>
      // <TextField
      //   sx={{ marginTop: "16px", fontFamily: "Roboto" }}
      //   label={label}
      //   placeholder={placeholder}
      //   disabled={disabled}
      //   value={value}
      //   onChange={onChange}
      //   helperText={helperText}
      // />
    );
  }
  if (isVisible && variant === "datepicker") {
    return (
      <Box sx={{ marginTop: "8px" }}>
        <DatePicker
          label={label}
          value={value}
          onChange={onChange}
          onAccept={(value) => {
            generateGtmEvent({
              event: "select",
              action: "click",
              name: "onclick",
              type: "select date",
              region: "main content",
              section: `${label} ^ calendar icon`,
              text: `${formatDate(new Date(value))}`,
            });
          }}
          onClose={() => {
            generateGtmEvent({
              event: "collapse",
              action: "close",
              name: "onclick",
              type: "click",
              region: "main content",
              section: `${label}`,
              text: "calendar icon",
              component: "class info",
            });
            setCalendarOpen(false);
          }}
          onOpen={() => {
            if (calendarOpen) return;
            generateGtmEvent({
              event: "collapse",
              action: "open",
              name: "onclick",
              type: "click",
              region: "main content",
              section: `${label}`,
              text: "calendar icon",
              component: "class info",
            });
            setCalendarOpen(true);
          }}
          renderInput={(params) => (
            <TextField
              onChange={(event) => {
                generateGtmEvent({
                  event: "form",
                  action: "click",
                  name: "onclick",
                  type: "blur",
                  region: "main content",
                  section: `${label}`,
                  text: event.target.value,
                  component: "class info",
                });
              }}
              {...params}
              required
              fullWidth
            />
          )}
          PopperProps={{
            sx: {
              zIndex: 9999,
            },
          }}
        />
      </Box>
    );
  }
  if (isVisible && variant === "dropdown") {
    return (
      <TextField
        sx={{ marginTop: "16px" }}
        select
        label={label}
        value={value}
        onChange={onChange}>
        {children}
      </TextField>
    );
  }

  return <></>;
};

export const ClassDetails = (props: ClassDetailsProps) => {
  const userCtx = useContext(UserContext) as UserCtx;
  const courseStructureCtx = useContext(CourseStructureContext) as CourseStructureCtx;
  const currentUser: DbUser = userCtx.dbUser;
  const { classDetails, updateCachedClassSettings } = props;
  const [loading, setLoading] = React.useState(true);
  const [observerList, setObserverList] = React.useState<any[]>([]);
  const [openShareModal, setOpenShareModal] = React.useState(false);
  const [classTitle, setClassTitle] = React.useState<string | null>(
    classDetails.settings?.nickname ?? null
  );

  // class editable fields for super/admin
  const [courseId, setCourseId] = React.useState<string>(
    classDetails.course_id ? `${classDetails.course_id}` : "1"
  );
  const [courseName, setCourseName] = React.useState<string>(
    `${classDetails.course_name}` ?? "1"
  );
  const [periodBlock, setPeriodBlock] = React.useState<string>(
    classDetails.period_block ?? ""
  );
  const [startDate, setStartDate] = React.useState<Date | null>(
    classDetails.start_date ?? null
  );
  const [endDate, setEndDate] = React.useState<Date | null>(
    classDetails.end_date ?? null
  );

  const [disableSave, setDisableSave] = React.useState(true);
  const [saving, setSaving] = React.useState(false);

  const defaultTitle = `${classDetails?.course_name} ${classDetails?.period_block}  ${
    classDetails?.start_date &&
    new Date(classDetails.start_date).toLocaleDateString("en-US", {
      timeZone: "UTC",
    })
  }`;

  const canShareClass =
    currentUser.role === Role.admin ||
    currentUser.role === Role.super ||
    (currentUser.role === Role.teacher && currentUser.id === classDetails.teacher_id);

  useEffect(() => {
    setClassTitle(classDetails.settings?.nickname ?? null);
    setCourseId(`${classDetails.course_id}` ?? "1");
    setCourseName(`${classDetails.course_name}` ?? "1");
    setPeriodBlock(classDetails.period_block ?? "");
    setStartDate(classDetails.start_date ?? null);
    setEndDate(classDetails.end_date ?? null);
  }, [
    classDetails.settings?.nickname,
    classDetails.course_id,
    classDetails.course_name,
    classDetails.period_block,
    classDetails.start_date,
    classDetails.end_date,
  ]);

  const handleSaveChanges = async () => {
    if (!saving) {
      setSaving(true);
      try {
        const updatedSettings = await userCtx.upsertUserClassSettings(classDetails.id, {
          nickname: classTitle && classTitle?.trim().length > 0 ? classTitle : null,
        });
        if (currentUser.role !== Role.teacher) {
          await axios.post<ClassRequest>("/class", {
            id: classDetails.id,
            teacher_id: classDetails.teacher_id,
            course_id: courseId ?? 1,
            course_name: courseName,
            period_block: periodBlock,
            start_date: startDate,
            end_date: endDate,
            school_id: classDetails.school_id,
          });
        }
        if (updatedSettings) {
          SuccessToast("Class settings saved successfully!");
          // update the cached class details to reflect new nickname
          if (updatedSettings) updateCachedClassSettings(updatedSettings);
        }
        setDisableSave(true);
      } catch (e: any) {
        ErrorToast("Failed to save class settings");
      }
    }
    setSaving(false);
  };

  useEffect(() => {
    const getClassObserver = async (Class: ClassExtended) => {
      try {
        let result = await getObservers(`classId=${Class?.id}`);
        setObserverList([
          // ...[
          //   {
          //     id: Class.teacher_id,
          //     full_name: Class.teacher_name,
          //     email: Class.teacher_email,
          //     role: "Teacher",
          //   },
          // ],
          ...result.teachers,
          ...result.observers,
        ]);
        setLoading(false);
      } catch (e: any) {
        let errorMessages = e.message as string;
        toast.error(errorMessages);
        setLoading(false);
      }
    };

    if (!classDetails) return;
    if (!classDetails.id) return;
    getClassObserver(classDetails);
    setClassTitle(classDetails.settings?.nickname ?? null);
  }, [classDetails]);

  const handleRemoveObserverAccess = async (observerId: string) => {
    try {
      await deleteObserver({
        type: "OBSERVER_CLASS",
        classId: classDetails?.id,
        grantToId: observerId,
      });
      let newObserverList = observerList.filter((observer) => observer.id !== observerId);
      setObserverList([...newObserverList]);
      SuccessToast("Observer removed successfully!");
    } catch (e) {
      console.log(e);
    }
  };

  return (
    <>
      <Stack flexDirection={"column"} alignItems={"left"} gap={"8px"}>
        <Stack flexDirection={"row"} alignItems={"center"}>
          <Typography
            sx={{
              font: "normal normal bold 28px/54px proxima-soft",
              letterSpacing: "0.28px",
              color: "#191919",
            }}>
            Class info
          </Typography>
          <ButtonZoom
            disabled={disableSave || saving}
            disableZoom={true}
            size={"small"}
            variant="contained"
            onClick={() => {
              HandleDataLayer(
                "link",
                "click",
                "internal link",
                "main content",
                "class info",
                "save changes",
                "onclick",
                "class settings"
              );
              handleSaveChanges();
            }}
            sx={{
              marginLeft: "auto",
              width: "auto",
              height: "34px",
              borderRadius: "8px",
              fontSize: "16px",
            }}>
            Save Changes
          </ButtonZoom>
        </Stack>
        <ClassDetailItems
          title={"Class name"}
          value={defaultTitle}
          isValueVisible={true}
        />
        <ClassDetailEditableItem
          variant={"text"}
          value={classTitle ?? ""}
          disabled={saving}
          label={"Nickname (optional)"}
          placeholder={"Nickname (optional)"}
          isVisible={currentUser.role === Role.teacher}
          onChange={(e) => {
            HandleDataLayer(
              "form",
              "click",
              "blur",
              "main content",
              "class name",
              e.target.value,
              "onclick",
              "class info"
            );
            setDisableSave(false);
            setClassTitle(e.target.value);
          }}
          helperText={"This is the class name you will see across your dashboard."}
        />
        <Divider sx={{ width: "100%" }} />
        <ClassDetailItems
          title={"Course"}
          value={classDetails.course_name}
          isValueVisible={
            !(currentUser.role === Role.admin || currentUser.role === Role.super)
          }
        />
        <ClassDetailEditableItem
          variant={"dropdown"}
          value={`${courseId}` ?? "1"}
          disabled={saving}
          label={"Course"}
          placeholder={"Course"}
          isVisible={currentUser.role === Role.admin || currentUser.role === Role.super}
          onChange={(e) => {
            let courseName =
              courseStructureCtx.allCourses.find(
                (item) => item.id === Number(e.target.value)
              )?.name ?? "";
            HandleDataLayer(
              "select",
              "click",
              "select course",
              "main content",
              "course",
              courseName,
              "onclick",
              "class info"
            );
            setDisableSave(false);
            setCourseId(`${e.target.value}`);
            setCourseName(courseName);
          }}>
          {courseStructureCtx.allCourses.map((item, index) => {
            return (
              <MenuItem key={`subject-${index}-${item.name}`} value={`${item.id}`}>
                {item.name}
              </MenuItem>
            );
          })}
        </ClassDetailEditableItem>
        <Divider sx={{ width: "100%" }} />
        <ClassDetailItems
          title={"Period/block"}
          value={classDetails.period_block}
          isValueVisible={
            !(currentUser.role === Role.admin || currentUser.role === Role.super)
          }
        />
        <ClassDetailEditableItem
          variant={"text"}
          value={periodBlock ?? ""}
          disabled={saving}
          label={"Period/block"}
          placeholder={"Period/block"}
          isVisible={currentUser.role === Role.admin || currentUser.role === Role.super}
          onChange={(e) => {
            HandleDataLayer(
              "form",
              "click",
              "blur",
              "main content",
              "Period/block",
              e.target.value,
              "onclick",
              "class info"
            );
            setDisableSave(false);
            setPeriodBlock(e.target.value);
          }}
        />
        <Divider sx={{ width: "100%" }} />
        <ClassDetailItems
          title={"Start date"}
          value={classDetails.start_date && formatDate(new Date(classDetails.start_date))}
          isValueVisible={
            !(currentUser.role === Role.admin || currentUser.role === Role.super)
          }
        />
        <ClassDetailEditableItem
          variant={"datepicker"}
          value={startDate && formatDate(new Date(startDate))}
          disabled={saving}
          label={"Start date"}
          placeholder={"Start date"}
          isVisible={currentUser.role === Role.admin || currentUser.role === Role.super}
          onChange={(e) => {
            // HandleDataLayer(
            //   "form",
            //   "click",
            //   "blur",
            //   "main content",
            //   "Start date",
            //   e && formatDate(new Date(e)),
            //   "onclick",
            //   "class info"
            // );
            setDisableSave(false);
            setStartDate(new Date(e));
          }}
        />
        <Divider sx={{ width: "100%" }} />
        <ClassDetailItems
          title={"End date"}
          value={classDetails.end_date && formatDate(new Date(classDetails.end_date))}
          isValueVisible={
            !(currentUser.role === Role.admin || currentUser.role === Role.super)
          }
        />
        <ClassDetailEditableItem
          variant={"datepicker"}
          value={endDate && formatDate(new Date(endDate))}
          disabled={saving}
          label={"End date"}
          placeholder={"End date"}
          isVisible={currentUser.role === Role.admin || currentUser.role === Role.super}
          onChange={(e) => {
            // HandleDataLayer(
            //   "form",
            //   "click",
            //   "blur",
            //   "main content",
            //   "End date",
            //   e && formatDate(new Date(e)),
            //   "onclick",
            //   "class info"
            // );
            setDisableSave(false);
            setEndDate(new Date(e));
          }}
        />
        <Box>
          <Typography
            sx={{
              font: "normal normal bold 28px/54px proxima-soft",
              letterSpacing: "0.28px",
              color: "#191919",
              marginTop: "48px",
            }}>
            People with access
          </Typography>
          <Stack flexDirection={"column"} alignItems={"left"} gap={"8px"}>
            {loading && (
              <Box sx={{ width: "100%", textAlign: "center" }}>
                <CircularProgress />
              </Box>
            )}
            {!loading &&
              observerList.map((teacher, index) => (
                <AccessListItems
                  key={`acl-item-${index}`}
                  removeAccess={handleRemoveObserverAccess}
                  teacher={teacher}
                  currentUser={currentUser}
                />
              ))}
          </Stack>
          {canShareClass && (
            <ButtonZoom
              disableZoom={true}
              size={"medium"}
              variant="outlined"
              onClick={() => {
                setOpenShareModal(true);
                HandleDataLayer(
                  "modal",
                  "open",
                  "click",
                  "main content",
                  "people with access",
                  "add observers",
                  "onclick"
                );
              }}
              sx={{
                marginTop: "24px",
                width: "auto",
                height: "62px",
                borderRadius: "16px",
                fontSize: "20px",
              }}>
              Add Observers
            </ButtonZoom>
          )}
        </Box>
      </Stack>
      <ShareAccessModal
        loading={loading}
        schoolId={classDetails.school_id!}
        open={openShareModal}
        handleClose={() => {
          setOpenShareModal(false);
        }}
        assignAccess={async (_accessList) => {
          if (_accessList.length > 0) {
            // await api call and send toast message
            try {
              const response = await addObservers(
                {
                  classId: classDetails?.id,
                  type: "OBSERVER_CLASS",
                },
                _accessList
              );
              if (response.some((res) => res.status === "fulfilled")) {
                let newObservers = response.map((res: any) => {
                  if (res.status === "fulfilled" && res.value) {
                    return {
                      id: res.value.user_id,
                      full_name: _accessList.find((user) => user.id === res.value.user_id)
                        ?.full_name,
                      email: _accessList.find((user) => user.id === res.value.user_id)
                        ?.email,
                      role: "Observer",
                    };
                  } else {
                    return null;
                  }
                });
                setObserverList([...observerList, ...newObservers]);
                SuccessToast("Observer added successfully!");
              }
              if (response.some((res) => res.status === "rejected")) {
                response.forEach((res) => {
                  if (res.status === "rejected") {
                    ErrorToast(res.reason);
                  }
                });
              }
            } catch (e) {
              ErrorToast("Error adding observers");
            }
          }
        }}
        currentUser={currentUser}
        existingObservers={observerList}
        title={defaultTitle}
        accessFor={"class"}
        removeAccess={handleRemoveObserverAccess}
      />
    </>
  );
};
